import { atom } from "jotai";
import { v4 as uuid } from "uuid";

// Alias for allowed snackbar message severity.
export type SnackbarSeverity = "success" | "warning" | "error" | "info";

/**
 * Defines the structure of a snackbar message.
 */
export type SnackbarMessage = {
	/** The message identifier */
	id: string;

	/** The message rendered by the snackbar */
	text: string;

	/**
	 * Controls the appearance of the message according to its severity (success,
	 * warning, error or info)
	 */
	severity: SnackbarSeverity;
};

// Use internally by read-only and write-only atoms.
const _snackbarMessages = atom<SnackbarMessage[]>([]);

/**
 * checkMessages returns the first message of the snackbar messages queue if it exists,
 * returns undefined otherwise.
 */
export const checkMessages = atom((get) => {
	const messages = get(_snackbarMessages);
	return messages.length > 0 ? messages[0] : undefined;
});

/**
 * publishSnackbarMessage publishes a new message by appending it to snackbar messages queue.
 */
export const publishSnackbarMessage = atom(null, (get, set, message: SnackbarMessage) =>
	set(_snackbarMessages, [...get(_snackbarMessages), message])
);

/**
 * consumeMessage removes the first message from snackbar messages queue if it exists,
 * does nothing otherwise.
 */
export const consumeMessage = atom(null, (get, set) => {
	const messages = get(_snackbarMessages);
	set(_snackbarMessages, messages.slice(1));
});

/**
 * makeSnackbarMessage is a utility functions that eases configuration of a SnackbarState value.
 *
 * @param text
 * @param severity         the optional duration of the message visibility
 * @returns                a value describing a SnackbarState
 */
export const makeSnackbarMessage = (text: string, severity: SnackbarSeverity = "info"): SnackbarMessage => {
	return { id: uuid(), text, severity };
};

/** The current view title */
export const viewTitle = atom<string>("Greenrouter");
